import { useQuery } from '@tanstack/react-query'
import { Menu, ChevronDown, ChevronRight, Coffee, Heart, Share2 } from 'lucide-react'
import { type ReactNode, useState, useEffect, Suspense } from 'react'
import { useTranslation } from 'react-i18next'
import { Link, useLocation } from 'react-router'

import { useMediaQuery } from '../hooks/useMediaQuery'
import WhatsNewModal from './dialogs/WhatsNewModal'
import FeedbackWidget from './FeedbackWidget'
import NotificationCenter from './NotificationCenter'
import SystemStatus from './SystemStatus'
import { ThemeToggle } from './ThemeToggle'
import { Button } from './ui/Button'
import { getFeatureFlags } from '../api/featureFlags'
import { checkHealth } from '../api/health'
import { getSettings } from '../api/settings'
import { useAuth } from '../hooks/useAuth'
import { toast } from '../utils/toast'


interface LayoutProps {
  children: ReactNode
}

type NavItem = {
  name: string
  path?: string
  icon?: string
  children?: NavItem[]
}

export default function Layout({ children }: LayoutProps) {
  const location = useLocation()
  const { t } = useTranslation()
  const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false)
  const [isCollapsed, setIsCollapsed] = useState(() => {
    const saved = localStorage.getItem('sidebarCollapsed')
    return saved ? JSON.parse(saved) : false
  })
  const [expandedMenus, setExpandedMenus] = useState<string[]>([])
  const { logout, user } = useAuth()
  const isMobile = useMediaQuery('(max-width: 1023px)')

  useEffect(() => {
    localStorage.setItem('sidebarCollapsed', JSON.stringify(isCollapsed))
  }, [isCollapsed])

  // Close the mobile sidebar on Escape — the overlay below is aria-hidden
  // (click-to-dismiss only), so keyboard users need this listener to dismiss it too.
  useEffect(() => {
    if (!mobileSidebarOpen) return
    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setMobileSidebarOpen(false)
      }
    }
    document.addEventListener('keydown', handleEscape)
    return () => document.removeEventListener('keydown', handleEscape)
  }, [mobileSidebarOpen])

  const toggleMenu = (name: string) => {
    setExpandedMenus(prev =>
      prev.includes(name)
        ? prev.filter(item => item !== name)
        : [...prev, name]
    )
  }

  const handleShareProject = async () => {
    const shareUrl = 'https://github.com/Wikid82/Charon'
    const shareData = {
      title: 'Charon',
      text: 'Charon is a self-hosted reverse proxy manager for securely exposing and managing your services.',
      url: shareUrl,
    }

    if (navigator.share) {
      try {
        await navigator.share(shareData)
        return
      } catch (err) {
        // A user cancelling the native share sheet is not an error — no-op silently.
        if (err instanceof Error && err.name === 'AbortError') {
          return
        }
        // Fall through to the clipboard fallback for any other share failure.
      }
    }

    try {
      await navigator.clipboard.writeText(shareUrl)
      toast.success('Link copied to clipboard')
    } catch {
      toast.error('Failed to copy link to clipboard')
    }
  }

  const { data: health } = useQuery({
    queryKey: ['health'],
    queryFn: checkHealth,
    staleTime: 1000 * 60 * 60, // 1 hour
  })

  const { data: featureFlags } = useQuery({
    queryKey: ['feature-flags'],
    queryFn: getFeatureFlags,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })

  const { data: settings } = useQuery({
    queryKey: ['settings'],
    queryFn: getSettings,
    staleTime: 1000 * 60 * 5, // 5 minutes
  })
  const customLogoUrl = settings?.['ui.logo_url'] ?? null
  const customBannerUrl = settings?.['ui.banner_url'] ?? null
  const logoSrc = customLogoUrl || '/logo.png'

  const navigation: NavItem[] = [
    { name: t('navigation.dashboard'), path: '/', icon: '📊' },
    { name: t('navigation.proxyHosts'), path: '/proxy-hosts', icon: '🌐' },
    { name: t('navigation.redirectionHosts'), path: '/redirection-hosts', icon: '↪️' },
    {
      name: t('navigation.hecate'),
      path: '/hecate',
      icon: '🔗',
      children: [
        { name: t('navigation.remoteServers'), path: '/hecate/remote-servers', icon: '🖥️' },
        { name: t('navigation.tunnels'),       path: '/hecate/tunnels',         icon: '🌐' },
        { name: t('navigation.providers'),     path: '/hecate/providers',       icon: '🔑' },
        ...(user?.role === 'admin' ? [{ name: t('navigation.agent'), path: '/hecate/agent', icon: '🤖' }] : []),
      ],
    },
    { name: t('navigation.domains'), path: '/domains', icon: '🌍' },
    { name: t('navigation.certificates'), path: '/certificates', icon: '🔒' },
    { name: t('navigation.dns'), path: '/dns', icon: '☁️', children: [
      { name: t('navigation.dnsProviders'), path: '/dns/providers', icon: '🧭' },
      { name: t('navigation.plugins'), path: '/dns/plugins', icon: '🔌' },
    ] },
    { name: t('navigation.uptime'), path: '/uptime', icon: '📈' },
    { name: t('navigation.cerberus'), path: '/security', icon: '🛡️', children: [
      { name: t('navigation.dashboard'), path: '/security', icon: '🛡️' },
      ...(user?.role === 'admin' ? [{ name: t('navigation.crowdsec'), path: '/security/crowdsec', icon: '🛡️' }] : []),
      { name: t('navigation.accessLists'), path: '/security/access-lists', icon: '🔒' },
      { name: t('navigation.rateLimiting'), path: '/security/rate-limiting', icon: '⚡' },
      { name: t('navigation.waf'), path: '/security/waf', icon: '🛡️' },
      { name: t('navigation.securityHeaders'), path: '/security/headers', icon: '🔐' },
      ...(user?.role === 'admin' ? [{ name: t('navigation.encryption'), path: '/security/encryption', icon: '🔑' }] : []),
    ]},
    {
      name: t('navigation.settings'),
      path: '/settings',
      icon: '⚙️',
      children: [
        { name: t('navigation.system'), path: '/settings/system', icon: '⚙️' },
        { name: t('navigation.notifications'), path: '/settings/notifications', icon: '🔔' },
        { name: t('navigation.email'), path: '/settings/smtp', icon: '📧' },
        ...(user?.role === 'admin' ? [{ name: t('navigation.users'), path: '/settings/users', icon: '👥' }] : []),
      ]
    },
    {
      name: t('navigation.tasks'),
      path: '/tasks',
      icon: '📋',
      children: [
        {
          name: t('navigation.import'),
          path: '/tasks/import',
          children: [
            { name: t('navigation.caddyfile'), path: '/tasks/import/caddyfile', icon: '📥' },
            { name: t('navigation.crowdsec'), path: '/tasks/import/crowdsec', icon: '🛡️' },
            { name: t('navigation.importNPM'), path: '/tasks/import/npm', icon: '📦' },
            { name: t('navigation.importJSON'), path: '/tasks/import/json', icon: '📄' },
          ]
        },
        { name: t('navigation.backups'), path: '/tasks/backups', icon: '💾' },
        { name: t('navigation.logs'), path: '/tasks/logs', icon: '📝' },
        ...(user?.role === 'admin' ? [{ name: t('navigation.database'), path: '/tasks/database', icon: '🗄️' }] : []),
      ]
    },
  ].filter(item => {
    // Passthrough users see no navigation — they're redirected to /passthrough
    if (user?.role === 'passthrough') return false
    // Optional Features Logic
    // Default to visible (true) if flags are loading or undefined
    if (item.name === t('navigation.uptime')) return featureFlags?.['feature.uptime.enabled'] !== false
    if (item.name === t('navigation.cerberus')) return featureFlags?.['feature.cerberus.enabled'] !== false
    return true
  })

  return (
    <div className="min-h-screen bg-surface-base flex transition-colors duration-200">
      {/* Skip to main content link for accessibility */}
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:absolute focus:z-50 focus:p-4 focus:bg-brand-500 focus:text-white focus:font-medium focus:rounded-md focus:m-2"
      >
        {t('accessibility.skipToContent')}
      </a>
      {/* Mobile Header */}
      {isMobile && <div className="lg:hidden fixed top-0 left-0 right-0 h-16 bg-surface-overlay border-b border-border flex items-center justify-between px-4 z-40">
        <div className="flex items-center gap-3">
          <Button variant="ghost" size="sm" onClick={() => setMobileSidebarOpen(!mobileSidebarOpen)} data-testid="mobile-menu-toggle">
            <Menu className="w-5 h-5" />
          </Button>
          <img src={logoSrc} alt="Charon" className="h-10 w-auto" />
        </div>
        <div className="flex items-center gap-2">
          <NotificationCenter />
          <ThemeToggle />
        </div>
      </div>}

      {/* Sidebar */}
      <aside className={`
        fixed lg:fixed inset-y-0 left-0 z-30 transform transition-all duration-200 ease-in-out
        bg-surface-overlay border-r border-border flex flex-col
        ${mobileSidebarOpen ? 'translate-x-0' : '-translate-x-full lg:translate-x-0'}
        ${isCollapsed ? 'w-20' : 'w-64'}
      `}>
        <div className={`h-20 flex items-center justify-center border-b border-border`}>
           {isCollapsed ? (
             <img src={logoSrc} alt="Charon" className="h-12 w-auto" fetchPriority="high" decoding="async" />
           ) : customBannerUrl ? (
             <img src={customBannerUrl} alt="Charon" className="h-14 w-auto max-w-[200px] object-contain" fetchPriority="high" decoding="async" />
           ) : (
             <picture>
               <source srcSet="/banner.webp" type="image/webp" />
               <img src="/banner.png" alt="Charon" className="h-14 w-auto max-w-[200px] object-contain" fetchPriority="high" decoding="async" />
             </picture>
           )}
        </div>

        <div className="flex flex-col flex-1 px-4 mt-16 lg:mt-6 min-h-0">
          <nav className="flex-1 space-y-1 overflow-y-auto">
            {navigation.map((item) => {
              if (item.children) {
                // Collapsible Group
                const isExpanded = expandedMenus.includes(item.name)
                const isActive = location.pathname.startsWith(item.path!)

                // If sidebar is collapsed, render as a simple link (icon only)
                if (isCollapsed) {
                  return (
                    <Link
                      key={item.name}
                      to={item.path!}
                      onClick={() => setMobileSidebarOpen(false)}
                      className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors justify-center ${
                        isActive
                          ? 'bg-brand-700 text-content-primary'
                          : 'text-content-secondary hover:bg-surface-muted hover:text-content-primary'
                      }`}
                      title={item.name}
                      aria-current={isActive ? 'page' : undefined}
                    >
                      <span className="text-lg">{item.icon}</span>
                    </Link>
                  )
                }

                // If sidebar is expanded, render as collapsible accordion
                return (
                  <div key={item.name} className="space-y-1">
                    <button
                      onClick={() => toggleMenu(item.name)}
                      aria-expanded={isExpanded}
                      className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                        isActive
                          ? 'text-brand-500'
                          : 'text-content-secondary hover:bg-surface-muted hover:text-content-primary'
                      }`}
                    >
                      <div className="flex items-center gap-3">
                        <span className="text-lg">{item.icon}</span>
                        <span>{item.name}</span>
                      </div>
                      {isExpanded ? (
                        <ChevronDown className="w-4 h-4" />
                      ) : (
                        <ChevronRight className="w-4 h-4" />
                      )}
                    </button>

                    {isExpanded && (
                      <div className="pl-11 space-y-1">
                        {item.children.map((child: NavItem) => {
                          // If this child has its own children, render a nested accordion
                          if (child.children && child.children.length > 0) {

                            const nestedExpandedKey = `${item.name}:${child.name}`
                            const isNestedOpen = expandedMenus.includes(nestedExpandedKey)
                            return (
                              <div key={child.path} className="space-y-1">
                                <button
                                  onClick={() => toggleMenu(nestedExpandedKey)}
                                  aria-expanded={isNestedOpen}
                                  className={`w-full flex items-center justify-between py-2 px-3 rounded-md text-sm transition-colors ${
                                    location.pathname.startsWith(child.path!)
                                      ? 'text-brand-500'
                                      : 'text-content-secondary hover:bg-surface-muted hover:text-content-primary'
                                  }`}
                                >
                                  <div className="flex items-center gap-2">
                                    <span className="text-lg">{child.icon}</span>
                                    <span>{child.name}</span>
                                  </div>
                                  {isNestedOpen ? (
                                    <ChevronDown className="w-4 h-4" />
                                  ) : (
                                    <ChevronRight className="w-4 h-4" />
                                  )}
                                </button>
                                {isNestedOpen && (
                                  <div className="pl-6 space-y-1">
                                    {child.children.map((sub: NavItem) => (
                                      <Link
                                        key={sub.path}
                                        to={sub.path!}
                                        onClick={() => setMobileSidebarOpen(false)}
                                        className={`block py-2 px-3 rounded-md text-sm transition-colors ${
                                          location.pathname === sub.path
                                            ? 'bg-brand-500/10 text-brand-500'
                                            : 'text-content-muted hover:text-content-primary hover:bg-surface-subtle/50'
                                        }`}
                                        aria-current={location.pathname === sub.path ? 'page' : undefined}
                                      >
                                        {sub.name}
                                      </Link>
                                    ))}
                                  </div>
                                )}
                              </div>
                            )
                          }
                          const isChildActive = location.pathname === child.path
                          return (
                            <Link
                              key={child.path}
                              to={child.path!}
                              onClick={() => setMobileSidebarOpen(false)}
                              className={`block py-2 px-3 rounded-md text-sm transition-colors ${
                                isChildActive
                                  ? 'bg-brand-500/10 text-brand-500'
                                  : 'text-content-muted hover:text-content-primary hover:bg-surface-subtle/50'
                              }`}
                              aria-current={isChildActive ? 'page' : undefined}
                            >
                              {child.name}
                            </Link>
                          )
                        })}
                      </div>
                    )}
                  </div>
                )
              }

              const isActive = location.pathname === item.path

              return (
                <Link
                  key={item.path}
                  to={item.path!}
                  onClick={() => setMobileSidebarOpen(false)}
                  className={`flex items-center gap-3 px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                    isActive
                      ? 'bg-brand-700 text-content-primary'
                      : 'text-content-secondary hover:bg-surface-muted hover:text-content-primary'
                  } ${isCollapsed ? 'justify-center' : ''}`}
                  title={isCollapsed ? item.name : ''}
                  aria-current={isActive ? 'page' : undefined}
                >
                  <span className="text-lg">{item.icon}</span>
                  {!isCollapsed && item.name}
                </Link>
              )
            })}
          </nav>

          <div className={`mt-2 border-t border-border pt-6 pb-3 shrink-0 ${isCollapsed ? 'hidden' : ''}`}>
            <div className="flex items-center justify-center gap-3 text-content-muted">
              <a
                href="https://github.com/sponsors/Wikid82"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Sponsor Charon on GitHub"
                className="hover:text-content-primary transition-colors"
              >
                <Heart className="w-4 h-4" />
              </a>
              <a
                href="https://buymeacoffee.com/Wikid82"
                target="_blank"
                rel="noopener noreferrer"
                aria-label="Support Charon on Buy Me a Coffee"
                className="hover:text-content-primary transition-colors"
              >
                <Coffee className="w-4 h-4" />
              </a>
              <button
                type="button"
                onClick={handleShareProject}
                aria-label="Share Charon"
                className="hover:text-content-primary transition-colors"
              >
                <Share2 className="w-4 h-4" />
              </button>
            </div>
            <div className="text-xs text-content-muted text-center mt-2 mb-2 flex flex-col gap-0.5">
              <span>Version {health?.version || 'dev'}</span>
              {health?.git_commit && health.git_commit !== 'unknown' && (
                <span className="text-[10px] opacity-75 font-mono">
                  ({health.git_commit.substring(0, 7)})
                </span>
              )}
            </div>
            <button
              onClick={() => {
                setMobileSidebarOpen(false)
                logout()
              }}
              className="mt-3 w-full flex items-center justify-center gap-2 px-4 py-3 rounded-lg text-sm font-medium transition-colors text-error bg-error-muted hover:bg-error/20"
            >
              <span className="text-lg">🚪</span>
              {t('auth.logout')}
            </button>
          </div>

          {/* Collapsed Logout */}
          {isCollapsed && (
             <div className="mt-2 border-t border-border pt-4 pb-4 shrink-0">
                <button
                  onClick={() => {
                    setMobileSidebarOpen(false)
                    logout()
                  }}
                  className="w-full flex items-center justify-center p-3 rounded-lg transition-colors text-error hover:bg-error-muted"
                  title={t('auth.logout')}
                >
                  <span className="text-lg">🚪</span>
                </button>
             </div>
          )}

        </div>
      </aside>

      {/* Overlay for mobile */}
            {/* Mobile Overlay */}
      {mobileSidebarOpen && (
        <div
          className="fixed inset-0 bg-gray-900/50 z-20 lg:hidden"
          aria-hidden="true"
          onClick={() => setMobileSidebarOpen(false)}
        />
      )}

      {/* Main Content */}
      <main id="main-content" tabIndex={-1} className={`flex-1 min-w-0 pt-16 lg:pt-0 flex flex-col transition-all duration-200 ${isCollapsed ? 'lg:ml-20' : 'lg:ml-64'}`}>
        {/* Desktop Header */}
        <header className="hidden lg:flex items-center justify-between px-8 h-20 bg-surface-overlay border-b border-border sticky top-0 z-10">
           <div className="w-1/3 flex items-center gap-4">
             <button
                onClick={() => setIsCollapsed(!isCollapsed)}
                className="p-2 rounded-lg text-content-muted hover:bg-surface-muted transition-colors"
                title={isCollapsed ? t('navigation.expandSidebar') : t('navigation.collapseSidebar')}
              >
                <Menu className="w-5 h-5" />
              </button>
           </div>
           <div className="w-1/3 flex justify-center">
             {/* Banner moved to sidebar */}
           </div>
           <div className="w-1/3 flex justify-end items-center gap-4">
             {user && (
               <Link to="/settings/users" className="text-sm font-medium text-content-secondary hover:text-brand-500 transition-colors">
                 {user.name}
               </Link>
             )}
             <SystemStatus />
             <NotificationCenter />
             <ThemeToggle />
           </div>
        </header>
        <div className="flex-1 overflow-y-auto">
          <div className="p-4 lg:p-8 max-w-7xl mx-auto w-full">
            <Suspense fallback={<div className="flex items-center justify-center h-full"><div className="animate-pulse text-gray-400">Loading...</div></div>}>
              {children}
            </Suspense>
          </div>
        </div>
      </main>
      <FeedbackWidget />
      <WhatsNewModal mode="status" />
    </div>
  )
}
